<template>
  <div class="cp-wrap">
    <div v-if="loading" class="cp-loading"><span class="cp-spin"></span> Loading content prompts…</div>

    <template v-else-if="hasAny">
      <!-- 1. Page Structure -->
      <div v-if="pageStructureRows.length" class="cp-card" :class="{ collapsed: collapsed.structure }">
        <div class="cp-card-head" @click="toggle('structure')">
          <h4>Page Structure</h4><v-tooltip top color="transparent"><template v-slot:activator="{ on, attrs }"><span class="cp-info" v-bind="attrs" v-on="on"></span></template><div class="white-bg-tooltip small-tooltip">These things probably won't affect rank but will help you create a better page</div></v-tooltip>
          <button class="chev" aria-label="Collapse">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
        <div class="cp-card-body">
          <table class="cp-struct">
            <thead><tr><th>Element</th><th>Current usage</th><th>Recommended range</th></tr></thead>
            <tbody>
              <tr v-for="(row, i) in pageStructureRows" :key="i">
                <td class="el">{{ row.label }}</td>
                <td>{{ row.current }}</td>
                <td class="use">{{ row.recommend }}</td>
              </tr>
            </tbody>
          </table>
          <div v-if="canShowMoreStructure" class="cp-showmore" @click="showMorePageStructure = !showMorePageStructure">
            {{ showMorePageStructure ? 'Show less ▴' : 'Show more ▾' }}
          </div>
        </div>
      </div>

      <!-- 2. Competitor Titles -->
      <div v-if="allTitles.length" class="cp-card" :class="{ collapsed: collapsed.titles }">
        <div class="cp-card-head" @click="toggle('titles')">
          <h4>Competitor Titles</h4><v-tooltip top color="transparent"><template v-slot:activator="{ on, attrs }"><span class="cp-info" v-bind="attrs" v-on="on"></span></template><div class="white-bg-tooltip small-tooltip">These are the page titles of your competitors that are displayed in Google. Use these titles for inspiration on creating a title that Google will like.</div></v-tooltip>
          <button class="chev" aria-label="Collapse">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
        <div class="cp-card-body">
          <a v-for="(t, i) in titles" :key="i" class="cp-link-row" href="#" @click.prevent>{{ t }}</a>
          <div v-if="allTitles.length > 7" class="cp-showmore" @click="showMoreTitles = !showMoreTitles">
            {{ showMoreTitles ? 'Show less ▴' : 'Show more ▾' }}
          </div>
        </div>
      </div>

      <!-- 3. Related Keywords -->
      <div v-if="keywords.length" class="cp-card" :class="{ collapsed: collapsed.keywords }">
        <div class="cp-card-head" @click="toggle('keywords')">
          <h4>Related Keywords</h4><v-tooltip top color="transparent"><template v-slot:activator="{ on, attrs }"><span class="cp-info" v-bind="attrs" v-on="on"></span></template><div class="white-bg-tooltip small-tooltip">If relevant, use these keywords where you can on your page. If the related keyword is important, consider using it in a subtitle.</div></v-tooltip>
          <button class="chev" aria-label="Collapse">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
        <div class="cp-card-body">
          <a v-for="(k, i) in keywords" :key="i" class="cp-link-row" href="#" @click.prevent>{{ k.query || k }}</a>
        </div>
      </div>

      <!-- 4. Related Questions -->
      <div v-if="allQuestions.length" class="cp-card" :class="{ collapsed: collapsed.questions }">
        <div class="cp-card-head" @click="toggle('questions')">
          <h4>Related Questions</h4><v-tooltip top color="transparent"><template v-slot:activator="{ on, attrs }"><span class="cp-info" v-bind="attrs" v-on="on"></span></template><div class="white-bg-tooltip small-tooltip">Use these questions to provide additional related content. Use them on your page if you need to expand your content. You can also use them to create related pages on your site and link those pages to your target page.</div></v-tooltip>
          <button class="chev" aria-label="Collapse">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
        <div class="cp-card-body">
          <div v-for="(q, i) in questions" :key="i" class="cp-q-row"><span>{{ q.question || q }}</span><span class="ch">▾</span></div>
          <div v-if="allQuestions.length > 6" class="cp-showmore" @click="showMoreQuestions = !showMoreQuestions">
            {{ showMoreQuestions ? 'Show less ▴' : 'Show more ▾' }}
          </div>
        </div>
      </div>

      <!-- 5. Competitor H2s -->
      <div v-if="h2Competitors.length" class="cp-card" :class="{ collapsed: collapsed.h2 }">
        <div class="cp-card-head" @click="toggle('h2')">
          <h4>Competitor H2s</h4><v-tooltip top color="transparent"><template v-slot:activator="{ on, attrs }"><span class="cp-info" v-bind="attrs" v-on="on"></span></template><div class="white-bg-tooltip small-tooltip">Use these H2s for inspiration for the different sections on your page.</div></v-tooltip>
          <button class="chev" aria-label="Collapse">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
        <div class="cp-sub">These are all the H2 tags on the pages of your competitors</div>
        <div class="cp-card-body">
          <template v-for="(c, i) in h2Competitors">
            <div :key="'u' + i" class="cp-url-row" @click="expandedH2 = expandedH2 === i ? null : i">
              <span class="txt">{{ c.url }}</span><span class="ch">▾</span>
            </div>
            <div v-if="expandedH2 === i" :key="'h' + i" class="cp-tag-list">
              <div v-for="(t, j) in (c.h2Texts || [])" :key="j" class="cp-tag-row"><span class="cp-tag">H2</span><span>{{ t }}</span></div>
            </div>
          </template>
          <div v-if="allH2Competitors.length > 7" class="cp-showmore" @click="showMoreH2 = !showMoreH2">
            {{ showMoreH2 ? 'Show less ▴' : 'Show more ▾' }}
          </div>
        </div>
      </div>

      <!-- 6. Competitor H3s -->
      <div v-if="h3Competitors.length" class="cp-card" :class="{ collapsed: collapsed.h3 }">
        <div class="cp-card-head" @click="toggle('h3')">
          <h4>Competitor H3s</h4><v-tooltip top color="transparent"><template v-slot:activator="{ on, attrs }"><span class="cp-info" v-bind="attrs" v-on="on"></span></template><div class="white-bg-tooltip small-tooltip">Use these H3s for inspiration for the different sections on your page.</div></v-tooltip>
          <button class="chev" aria-label="Collapse">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M6 9l6 6 6-6"/></svg>
          </button>
        </div>
        <div class="cp-sub">These are all the H3 tags on the pages of your competitors</div>
        <div class="cp-card-body">
          <template v-for="(c, i) in h3Competitors">
            <div :key="'u' + i" class="cp-url-row" @click="expandedH3 = expandedH3 === i ? null : i">
              <span class="txt">{{ c.url }}</span><span class="ch">▾</span>
            </div>
            <div v-if="expandedH3 === i" :key="'h' + i" class="cp-tag-list">
              <div v-for="(t, j) in (c.h3Texts || [])" :key="j" class="cp-tag-row"><span class="cp-tag">H3</span><span>{{ t }}</span></div>
            </div>
          </template>
          <div v-if="allH3Competitors.length > 7" class="cp-showmore" @click="showMoreH3 = !showMoreH3">
            {{ showMoreH3 ? 'Show less ▴' : 'Show more ▾' }}
          </div>
        </div>
      </div>
    </template>

    <div v-else class="cp-empty">No content prompts available for this report.</div>
  </div>
</template>

<script>
// Content-brief guidance for the AI Writer v2 sidebar ("Content prompts" tab),
// styled per the redesign. Data comes from the report's PageRun (fetched by the
// parent sidebar and passed in). The page-structure ranges reuse the existing
// content-brief mixin so the "Use between X and Y" logic stays identical.
import { mixin as cbMixin } from "@/components/FullPageSourceEditorV2/components/side-pane/ContentBrief/cb_mixin.js";

export default {
  name: "AiwV2SidebarContentPrompts",
  mixins: [cbMixin],
  props: {
    pageRun: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      // cb_mixin inputs
      selectedMainTab: "pro",
      showMorePageStructure: false,
      // local card state — cards start minimized (collapsed) by default.
      collapsed: { structure: true, titles: true, keywords: true, questions: true, h2: true, h3: true },
      showMoreTitles: false,
      showMoreQuestions: false,
      showMoreH2: false,
      showMoreH3: false,
      expandedH2: null,
      expandedH3: null,
    };
  },
  computed: {
    // Feeds cb_mixin's page-structure computeds. No live draft tag counts here,
    // so current usage is 0 — the recommended ranges are the point.
    tagTotalData() {
      return ((this.pageRun && this.pageRun.tagCounts) || []).map((r) => ({ ...r, signalCnt: 0 }));
    },
    pageStructureRows() {
      return this.filteredPageStructureRecommendations || [];
    },
    canShowMoreStructure() {
      return (this.pageStructureRecommendations || []).length > 5;
    },
    competitors() {
      // SERP results (url/title/query) — used for the "Competitor Titles" card.
      return (this.pageRun && this.pageRun.gCompetitors) || [];
    },
    competitorPages() {
      // Scraped competitor pages (PageCompetitor) — these carry h2Texts/h3Texts.
      return (this.pageRun && this.pageRun.competitors) || [];
    },
    allTitles() {
      return this.competitors.map((c) => c.title).filter(Boolean);
    },
    titles() {
      return this.showMoreTitles ? this.allTitles : this.allTitles.slice(0, 7);
    },
    keywords() {
      return (this.pageRun && this.pageRun.relatedSearches) || [];
    },
    allQuestions() {
      return (this.pageRun && this.pageRun.relatedQuestions) || [];
    },
    questions() {
      return this.showMoreQuestions ? this.allQuestions : this.allQuestions.slice(0, 6);
    },
    allH2Competitors() {
      return this.competitorPages.filter((c) => (c.h2Texts || []).length);
    },
    h2Competitors() {
      return this.showMoreH2 ? this.allH2Competitors : this.allH2Competitors.slice(0, 7);
    },
    allH3Competitors() {
      return this.competitorPages.filter((c) => (c.h3Texts || []).length);
    },
    h3Competitors() {
      return this.showMoreH3 ? this.allH3Competitors : this.allH3Competitors.slice(0, 7);
    },
    hasAny() {
      return !!(this.pageStructureRows.length || this.allTitles.length || this.keywords.length
        || this.allQuestions.length || this.allH2Competitors.length || this.allH3Competitors.length);
    },
  },
  methods: {
    toggle(card) {
      this.$set(this.collapsed, card, !this.collapsed[card]);
    },
  },
};
</script>

<style scoped>
.cp-wrap { display: flex; flex-direction: column; gap: 10px; }
.cp-loading { padding: 16px 4px; font-size: 12.5px; color: var(--text-3, #6b7b7e); display: flex; align-items: center; gap: 8px; }
.cp-spin { width: 14px; height: 14px; border: 2px solid var(--border, #e4e4e4); border-top-color: var(--pop-orange, #ff5700); border-radius: 50%; display: inline-block; animation: cpspin .7s linear infinite; }
@keyframes cpspin { to { transform: rotate(360deg); } }
.cp-empty { padding: 16px 4px; font-size: 12.5px; color: var(--text-3, #6b7b7e); }

/* Content-prompt cards (design handoff) */
.cp-card { background: #fff; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); overflow: hidden; }
.cp-card-head { display: flex; align-items: center; gap: 7px; padding: 14px; cursor: pointer; user-select: none; }
.cp-card-head h4 { font-family: inherit; font-size: 14px; font-weight: 800; color: var(--text, #052e33); margin: 0; line-height: 1; }
.cp-card-head .chev { margin-left: auto; flex-shrink: 0; width: 24px; height: 24px; border: none; background: transparent; cursor: pointer; color: var(--text-3, #6b7b7e); display: inline-flex; align-items: center; justify-content: center; border-radius: 6px; transition: background .12s, transform .2s; }
.cp-card-head .chev svg { width: 16px; height: 16px; display: block; }
.cp-card-head:hover .chev { background: var(--surface-2, #f7f7f7); color: var(--text-2, #014751); }
.cp-card.collapsed .chev { transform: rotate(-90deg); }
.cp-card.collapsed .cp-card-body, .cp-card.collapsed .cp-sub { display: none; }
.cp-sub { font-size: 12px; color: var(--text-3, #6b7b7e); padding: 0 14px 12px; margin-top: -6px; }
.cp-card-body { border-top: 1px solid var(--border, #e4e4e4); }

/* Masked-SVG info glyph (matches the sidebar's .sb-info) */
.cp-info { width: 15px; height: 15px; display: inline-block; flex-shrink: 0; background-color: #014751; -webkit-mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat; mask: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>') center / contain no-repeat; }

.cp-link-row { display: block; padding: 12px 14px; border-bottom: 1px solid var(--border, #e4e4e4); color: var(--pop-blue, #316bff); font-size: 13px; font-weight: 700; text-decoration: none; line-height: 1.35; }
.cp-link-row:last-child { border-bottom: none; }
.cp-link-row:hover { text-decoration: underline; }
.cp-url-row { display: flex; align-items: flex-start; gap: 10px; padding: 12px 14px; border-bottom: 1px solid var(--border, #e4e4e4); font-size: 12.5px; color: var(--text-2, #014751); line-height: 1.4; cursor: pointer; }
.cp-url-row .txt { word-break: break-word; }
.cp-url-row .ch { margin-left: auto; flex-shrink: 0; color: var(--pop-orange, #ff5700); font-size: 11px; margin-top: 2px; }
.cp-q-row { display: flex; align-items: center; gap: 10px; padding: 12px 14px; border-bottom: 1px solid var(--border, #e4e4e4); font-size: 12.5px; color: var(--text-2, #014751); }
.cp-q-row:last-child { border-bottom: none; }
.cp-q-row .ch { margin-left: auto; flex-shrink: 0; color: var(--pop-orange, #ff5700); font-size: 11px; }
.cp-tag-list { padding: 4px 14px 8px 24px; border-bottom: 1px solid var(--border, #e4e4e4); }
.cp-tag-row { display: flex; align-items: center; gap: 8px; padding: 6px 0; font-size: 12.5px; color: var(--text-2, #014751); line-height: 1.4; }
.cp-tag { font-size: 10px; font-weight: 700; color: var(--pop-blue, #316bff); background: var(--pop-blue-soft, #c7ddff); border-radius: 3px; padding: 2px 6px; flex-shrink: 0; }
.cp-showmore { display: flex; align-items: center; justify-content: flex-end; gap: 5px; padding: 11px 14px; color: var(--text-3, #6b7b7e); font-size: 12.5px; font-weight: 600; cursor: pointer; }
.cp-struct { width: 100%; border-collapse: collapse; }
.cp-struct th { text-align: left; font-size: 11px; font-weight: 600; color: var(--text-3, #6b7b7e); padding: 12px 14px 8px; }
.cp-struct td { padding: 11px 14px; border-top: 1px solid var(--border, #e4e4e4); font-size: 12.5px; color: var(--text, #052e33); }
.cp-struct td.el { font-weight: 700; }
.cp-struct td.use { color: var(--text-2, #014751); }
</style>
